import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api, { getShortUrl } from '../lib/api';
import { toast } from 'sonner';
import CreateLinkModal from '../components/CreateLinkModal';
import BulkImportModal from '../components/BulkImportModal';
import HealthBadge from '../components/HealthBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import '../styles/design-system.css';
import '../styles/pages.css';
import './LinksPage.css';

const IP = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = {
  Search: (p) => (<svg width="14" height="14" {...IP} {...p}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>),
  Close: (p) => (<svg width="12" height="12" {...IP} {...p}><path d="M18 6 6 18M6 6l12 12" /></svg>),
  Plus: (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M5 12h14M12 5v14" /></svg>),
  Upload: (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>),
  Copy: (p) => (<svg width="14" height="14" {...IP} {...p}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>),
  Trash: (p) => (<svg width="14" height="14" {...IP} {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>),
  Chart: (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M3 3v18h18" /><path d="m7 14 4-4 4 4 5-5" /></svg>),
  Lock: (p) => (<svg width="11" height="11" {...IP} {...p}><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>),
  Clock: (p) => (<svg width="11" height="11" {...IP} {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>),
  Flask: (p) => (<svg width="11" height="11" {...IP} {...p}><path d="M10 2v7L4.5 19a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L14 9V2" /><path d="M8 2h8" /></svg>),
  Funnel: (p) => (<svg width="12" height="12" {...IP} {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>),
  Sort: (p) => (<svg width="12" height="12" {...IP} {...p}><path d="M3 6h18M6 12h12M10 18h4" /></svg>),
  ChevronDown: (p) => (<svg width="10" height="10" {...IP} {...p}><polyline points="6 9 12 15 18 9" /></svg>),
  ArrowRight: (p) => (<svg width="12" height="12" {...IP} {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>),
  ArrowLeft: (p) => (<svg width="12" height="12" {...IP} {...p}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>),
};

const STATUS_OPTIONS = [
  { value: '',         label: 'All status' },
  { value: 'active',   label: 'Active' },
  { value: 'expired',  label: 'Expired' },
  { value: 'archived', label: 'Archived' },
  { value: 'broken',   label: 'Broken links' },
];

const SORT_OPTIONS = [
  { by: 'created_at',   order: 'desc', label: 'Newest first' },
  { by: 'created_at',   order: 'asc',  label: 'Oldest first' },
  { by: 'total_clicks', order: 'desc', label: 'Most clicks' },
  { by: 'last_clicked', order: 'desc', label: 'Last clicked' },
];

const FAV_VARIANTS = ['', 'olive', 'dark', 'mustard'];

function statusTagClass(status) {
  switch (status) {
    case 'expired':  return 'row-tag coral';
    case 'archived': return 'row-tag gray';
    case 'active':
    default:         return 'row-tag olive';
  }
}

function scoreTone(score) {
  if (score >= 70) return 'olive';
  if (score >= 40) return 'mustard';
  return 'coral';
}

export default function LinksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [links, setLinks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(searchParams.get('new') === '1');
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const selectAllRef = useRef(null);

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/links', { params });
      setLinks(res.data.links || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load links', err);
      toast.error(err.message || 'Failed to load links');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(fetchLinks, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchLinks, search]);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectAllRef.current) return;
    const all = links.length > 0 && selectedLinks.length === links.length;
    const some = selectedLinks.length > 0 && !all;
    selectAllRef.current.indeterminate = some;
  }, [selectedLinks, links]);

  const handleCopy = async (link) => {
    const text = typeof link === 'object' ? getShortUrl(link) : getShortUrl(link);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    toast.success('Short URL copied to clipboard');
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Delete this link? This cannot be undone.')) return;
    try {
      await api.delete(`/links/${linkId}`);
      toast.success('Link deleted');
      fetchLinks();
    } catch {
      toast.error('Failed to delete link');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedLinks.length} links?`)) return;
    try {
      await api.post('/links/bulk-delete', { link_ids: selectedLinks });
      toast.success(`${selectedLinks.length} links deleted`);
      setSelectedLinks([]);
      fetchLinks();
    } catch {
      toast.error('Failed to delete links');
    }
  };

  const toggleSelect = (linkId) => {
    setSelectedLinks((prev) =>
      prev.includes(linkId) ? prev.filter((id) => id !== linkId) : [...prev, linkId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLinks.length === links.length && links.length > 0) {
      setSelectedLinks([]);
    } else {
      setSelectedLinks(links.map((l) => l.link_id));
    }
  };

  const totalPages = Math.ceil(total / 20);
  const totalClicks = links.reduce((acc, l) => acc + (l.total_clicks || 0), 0);
  const activeCount = links.filter((l) => l.status !== 'archived' && l.status !== 'expired').length;
  const avgScore = links.length
    ? Math.round(links.reduce((a, l) => a + (l.link_score || 0), 0) / links.length)
    : 0;

  const statusLabel = STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || 'All status';
  const sortLabel = SORT_OPTIONS.find((o) => o.by === sortBy && o.order === sortOrder)?.label || 'Sort';

  return (
    <div className="ds-page lflk-root" data-testid="links-page">
      {/* ============================  HEADER  ============================ */}
      <div className="page-header">
        <div>
          <div className="page-label mustard">Library · {total.toLocaleString()} links</div>
          <h1 className="page-title">All your <em>links</em>.</h1>
          <p className="page-sub">
            Search, filter, and manage every short link in your workspace.
          </p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button
            type="button"
            className="btn-outline"
            onClick={() => setShowImport(true)}
          >
            <Icon.Upload /> Import CSV
          </button>
          <button
            type="button"
            className="btn-coral"
            onClick={() => setShowCreate(true)}
            data-testid="create-link-btn"
          >
            <Icon.Plus /> New link
          </button>
        </div>
      </div>

      {/* ============================  TOOLBAR  ============================ */}
      <div className="links-toolbar">
        <div className="links-search">
          <Icon.Search />
          <input
            type="search"
            placeholder="Search by slug, destination, tag…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            data-testid="search-input"
          />
          {search && (
            <button
              type="button"
              style={{background:'none',border:'none',cursor:'pointer',color:'var(--ink-faint)',padding:0}}
              onClick={() => { setSearch(''); setPage(1); }}
              aria-label="Clear search"
            >
              <Icon.Close />
            </button>
          )}
        </div>
        <div style={{display:'flex',gap:8}}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="filter-chip" data-testid="status-filter">
                <Icon.Funnel /> {statusLabel}
                <Icon.ChevronDown />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {STATUS_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value || 'all'}
                  onClick={() => { setStatusFilter(opt.value); setPage(1); }}
                >
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="filter-chip" data-testid="sort-btn">
                <Icon.Sort /> {sortLabel}
                <Icon.ChevronDown />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {SORT_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.label}
                  onClick={() => { setSortBy(opt.by); setSortOrder(opt.order); }}
                >
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ============================  BULK BAR  ============================ */}
      {selectedLinks.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--ink)', color: 'white',
          borderRadius: 10, padding: '10px 18px', marginBottom: 12,
          fontSize: 13, fontWeight: 500,
        }}>
          <span style={{ flex: 1 }}>{selectedLinks.length} selected</span>
          <button
            type="button"
            style={{ background: 'var(--coral)', color: 'white', border: 'none', borderRadius: 7, padding: '6px 14px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handleBulkDelete}
            data-testid="bulk-delete-btn"
          >
            <Icon.Trash /> Delete selected
          </button>
          <button
            type="button"
            style={{ background: 'none', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}
            onClick={() => setSelectedLinks([])}
          >
            Clear
          </button>
        </div>
      )}

      {/* ============================  TABLE  ============================ */}
      <div className="full-table-wrap">
        <div className="full-table-head">
          <div>Link</div>
          <div>Domain</div>
          <div>Clicks</div>
          <div>Status</div>
          <div>Score</div>
          <div>Created</div>
          <div />
        </div>

        {loading ? (
          <div className="ds-empty">
            <div className="ds-skeleton" style={{ height: 20, width: 180, marginBottom: 12 }} />
            <div className="ds-skeleton" style={{ height: 12, width: 260 }} />
          </div>
        ) : links.length === 0 ? (
          <div className="ds-empty">
            <div className="ds-empty-icon">
              <svg width="22" height="22" {...IP}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
            </div>
            <div className="ds-empty-title">
              {search ? 'No matching links' : 'No links yet'}
            </div>
            <p className="ds-empty-sub">
              {search
                ? 'Try a different search term, or clear filters.'
                : 'Create your first short URL — AI suggestions, custom slugs and analytics built in.'}
            </p>
            {!search && (
              <button
                type="button"
                className="btn-coral"
                style={{ marginTop: 16 }}
                onClick={() => setShowCreate(true)}
              >
                <Icon.Plus /> Create your first link
              </button>
            )}
          </div>
        ) : (
          links.map((link, i) => {
            const score = link.link_score || 0;
            const selected = selectedLinks.includes(link.link_id);
            const domain = link.custom_domain || (window.LINKFLOW_DOMAIN || 'lf.link');
            return (
              <div
                key={link.link_id}
                className="full-table-row"
                style={selected ? { background: 'rgba(255,84,54,0.04)', outline: '1px solid rgba(255,84,54,0.2)' } : {}}
                data-testid={`link-row-${link.slug}`}
              >
                {/* Link */}
                <div className="row-link">
                  <div
                    className={`row-favicon ${FAV_VARIANTS[i % 4]}`}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(link.link_id); }}
                    style={{ cursor: 'pointer', position: 'relative' }}
                    title={selected ? 'Deselect' : 'Select'}
                  >
                    {selected ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    ) : (link.slug || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="row-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Link
                        to={`/links/${link.link_id}`}
                        className="row-slug"
                        style={{ textDecoration: 'none' }}
                        data-testid={`link-slug-${link.slug}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        /{link.slug}
                      </Link>
                      {link.password && (
                        <span title="Password protected" style={{ color: 'var(--ink-faint)', display: 'flex' }}>
                          <Icon.Lock />
                        </span>
                      )}
                      {link.expiry_date && (
                        <span title="Has expiry" style={{ color: 'var(--ink-faint)', display: 'flex' }}>
                          <Icon.Clock />
                        </span>
                      )}
                      {link.ab_test?.enabled && (
                        <span className="row-tag coral" data-testid={`ab-test-badge-${link.slug}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <Icon.Flask /> A/B
                        </span>
                      )}
                    </div>
                    <div className="row-dest">→ {link.destination_url}</div>
                    {(link.tags && link.tags.length > 0) && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                        {(link.tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="row-tag gray">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Domain */}
                <div className="row-domain">{domain}</div>

                {/* Clicks */}
                <div className="row-num">{(link.total_clicks || 0).toLocaleString()}</div>

                {/* Status */}
                <div>
                  <span className={statusTagClass(link.status)}>{link.status || 'active'}</span>
                  {link.health_status && link.health_status !== 'unknown' && (
                    <HealthBadge health={link.health_status} size="sm" />
                  )}
                </div>

                {/* Score */}
                <div>
                  {score > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{ flex: 1, height: 3, background: 'var(--line)', borderRadius: 2, overflow: 'hidden' }}>
                        <div
                          style={{
                            height: '100%',
                            borderRadius: 2,
                            transformOrigin: 'left',
                            transform: `scaleX(${Math.min(score, 100) / 100})`,
                            transition: 'transform 0.6s ease-out',
                            background: score >= 70 ? 'var(--olive)' : score >= 40 ? 'var(--mustard)' : 'var(--coral)',
                          }}
                        />
                      </div>
                      <span className={`row-tag ${scoreTone(score)}`}>{score}</span>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--ink-faint)', fontSize: 13 }}>—</span>
                  )}
                </div>

                {/* Created */}
                <div style={{ fontSize: 12, color: 'var(--ink-faint)', fontFamily: "'Geist Mono',monospace" }}>
                  {link.created_at?.slice(0, 10) || '—'}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="row-more" title="Actions">⋯</button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleCopy(link)} data-testid={`copy-btn-${link.slug}`}>
                        <Icon.Copy style={{ marginRight: 8 }} /> Copy URL
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/links/${link.link_id}`)} data-testid={`view-btn-${link.slug}`}>
                        <Icon.Chart style={{ marginRight: 8 }} /> View analytics
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(link.link_id)}
                        data-testid={`delete-btn-${link.slug}`}
                        style={{ color: 'var(--coral)' }}
                      >
                        <Icon.Trash style={{ marginRight: 8 }} /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })
        )}

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderTop: '1px solid var(--line)', fontSize: 13, color: 'var(--ink-faint)' }}>
            <span>Page {page} of {totalPages} · {total.toLocaleString()} total</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-outline"
                style={{ padding: '6px 14px', fontSize: 12 }}
                disabled={page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                <Icon.ArrowLeft /> Previous
              </button>
              <button
                type="button"
                className="btn-outline"
                style={{ padding: '6px 14px', fontSize: 12 }}
                disabled={page >= totalPages}
                onClick={() => setPage(Math.min(totalPages, page + 1))}
              >
                Next <Icon.ArrowRight />
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateLinkModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          fetchLinks();
          toast.success('Link created!');
        }}
      />

      <BulkImportModal
        open={showImport}
        onOpenChange={setShowImport}
        onImportComplete={() => {
          fetchLinks();
          toast.success('Import complete — links refreshed');
        }}
      />
    </div>
  );
}
